/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

interface InProgressOfferGuardModalProps {
    offerId: number
    /** Called when user wants to delete the in-progress offer and then leave */
    onDeleteAndLeave: () => Promise<void> | void
    /** Called when user wants to keep the offer (as draft) and leave */
    onKeepAndLeave: () => void
    /** Called when user wants to stay in the wizard */
    onStay: () => void
}

const InProgressOfferGuardModal: React.FC<InProgressOfferGuardModalProps> = ({
    offerId,
    onDeleteAndLeave,
    onKeepAndLeave,
    onStay,
}) => {
    const { t } = useTranslation()
    const [isDeleting, setIsDeleting] = useState(false)

    const handleDeleteAndLeave = async () => {
        setIsDeleting(true)
        try {
            await onDeleteAndLeave()
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <div className="InProgressOfferGuardModal p-6 sm:p-7 flex flex-col gap-6">
            <div className="flex items-start gap-4">
                <div className="mt-0.5 shrink-0 rounded-xl bg-amber-50 p-2.5 dark:bg-amber-950/40">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        {t('Unfinished Offer')}
                    </h3>
                    <p className="mt-1.5 text-sm leading-6 text-slate-600 dark:text-slate-300">
                        {t(
                            'Offer #{{id}} was already created but is incomplete (missing package items or upsells). What would you like to do?',
                            { id: offerId }
                        )}
                    </p>
                </div>
            </div>

            <div className="border-t border-slate-200 pt-5 dark:border-slate-700">
                <div className="flex flex-col gap-3">
                    <UiButton
                        style={UiButtonStyle.Gray}
                        size={UiButtonSize.Medium}
                        disabled={isDeleting}
                        onClick={onStay}
                        className="w-full justify-center"
                    >
                        {t('Stay in Wizard')}
                    </UiButton>
                    <UiButton
                        style={UiButtonStyle.Secondary}
                        size={UiButtonSize.Medium}
                        disabled={isDeleting}
                        onClick={onKeepAndLeave}
                        className="w-full justify-center"
                    >
                        {t('Keep as Draft & Leave')}
                    </UiButton>
                    <UiButton
                        style={UiButtonStyle.DELETE}
                        size={UiButtonSize.Medium}
                        loading={isDeleting}
                        disabled={isDeleting}
                        onClick={handleDeleteAndLeave}
                        className="w-full justify-center"
                    >
                        {t('Delete Offer & Leave')}
                    </UiButton>
                </div>
            </div>
        </div>
    )
}

export default InProgressOfferGuardModal
